/**
 * Service Worker integration utilities for ad caching and offline functionality
 */

export interface AdCacheStatus {
  adCacheSize: number;
  fallbackCacheSize: number;
  totalCachedAds: number;
  lastUpdated?: Date;
}

export interface AdCacheOptions {
  maxAge?: number; // in milliseconds
  maxSize?: number; // maximum number of cached ads
  priority?: 'high' | 'medium' | 'low';
}

/**
 * Service Worker Ad Integration class
 */
export class AdServiceWorkerIntegration {
  private serviceWorker: ServiceWorker | null = null;
  private isSupported: boolean = false;

  constructor() {
    this.isSupported = 'serviceWorker' in navigator;
    this.initializeServiceWorker();
  }

  /**
   * Initialize service worker connection
   */
  private async initializeServiceWorker(): Promise<void> {
    if (!this.isSupported) {
      console.warn('Service Worker not supported - ad caching disabled');
      return;
    }

    try {
      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready;
      this.serviceWorker = registration.active;

      // Listen for service worker updates
      registration.addEventListener('updatefound', () => {
        console.log('Ad Service Worker: Update found');
      });

      console.log('Ad Service Worker: Integration initialized');
    } catch (error) {
      console.error('Ad Service Worker: Failed to initialize:', error);
    }
  }

  /**
   * Cache an ad creative for offline use
   */
  async cacheAdCreative(
    url: string, 
    html: string, 
    metadata: any = {},
    options: AdCacheOptions = {}
  ): Promise<boolean> {
    if (!this.isSupported || !navigator.serviceWorker.controller) {
      console.warn('Service Worker not available for ad caching');
      return false;
    }

    try {
      const payload = {
        url,
        html,
        metadata: {
          ...metadata,
          cachedAt: Date.now(),
          maxAge: options.maxAge || 24 * 60 * 60 * 1000, // 24 hours default
          priority: options.priority || 'medium'
        }
      };

      navigator.serviceWorker.controller?.postMessage({
        type: 'CACHE_AD_CREATIVE',
        payload
      });

      console.log('Ad cached for offline use:', url);
      return true;
    } catch (error) {
      console.error('Failed to cache ad creative:', error);
      return false;
    }
  }

  /**
   * Cache multiple ad creatives in batch
   */
  async batchCacheAdCreatives(
    ads: Array<{
      url: string;
      html: string;
      metadata?: any;
      options?: AdCacheOptions;
    }>
  ): Promise<number> {
    if (!this.isSupported) return 0;

    let successCount = 0;
    const batchSize = 5; // Process in batches to avoid overwhelming the service worker

    for (let i = 0; i < ads.length; i += batchSize) {
      const batch = ads.slice(i, i + batchSize);
      
      const batchPromises = batch.map(ad => 
        this.cacheAdCreative(ad.url, ad.html, ad.metadata, ad.options)
      );

      const results = await Promise.allSettled(batchPromises);
      successCount += results.filter(result => 
        result.status === 'fulfilled' && result.value === true
      ).length;

      // Small delay between batches
      if (i + batchSize < ads.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    console.log(`Batch cached ${successCount}/${ads.length} ad creatives`);
    return successCount;
  }

  /**
   * Get ad cache status
   */
  async getAdCacheStatus(): Promise<AdCacheStatus | null> {
    if (!this.isSupported || !navigator.serviceWorker.controller) {
      return null;
    }

    return new Promise((resolve) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        const data = event.data;
        if (data.error) {
          console.error('Failed to get ad cache status:', data.error);
          resolve(null);
        } else {
          resolve({
            ...data,
            lastUpdated: new Date()
          });
        }
      };
      
      navigator.serviceWorker.controller?.postMessage(
        { type: 'GET_AD_CACHE_STATUS' },
        [messageChannel.port2]
      );
      
      // Timeout after 5 seconds
      setTimeout(() => {
        resolve(null);
      }, 5000);
    });
  }

  /**
   * Clear ad cache
   */
  async clearAdCache(): Promise<boolean> {
    if (!this.isSupported || !navigator.serviceWorker.controller) {
      return false;
    }

    try {
      navigator.serviceWorker.controller?.postMessage({
        type: 'CLEAR_AD_CACHE'
      });
      
      console.log('Ad cache cleared');
      return true;
    } catch (error) {
      console.error('Failed to clear ad cache:', error);
      return false;
    }
  }

  /**
   * Preload critical ad scripts
   */
  async preloadAdScripts(): Promise<void> {
    if (!this.isSupported) return;

    const criticalScripts = [
      'https://cdn.jsdelivr.net/npm/prebid.js@latest/dist/prebid.js',
      'https://securepubads.g.doubleclick.net/tag/js/gpt.js',
      'https://c.amazon-adsystem.com/aax2/apstag.js'
    ];

    try {
      // Use service worker to cache scripts
      const cache = await caches.open('fashion-rating-ads-v1');
      await cache.addAll(criticalScripts);
      console.log('Critical ad scripts preloaded');
    } catch (error) {
      console.warn('Failed to preload ad scripts:', error);
    }
  }

  /**
   * Setup offline ad fallbacks
   */
  async setupOfflineFallbacks(): Promise<void> {
    if (!this.isSupported) return;

    const fallbackAds = [
      {
        key: 'banner-728x90',
        html: this.generateFallbackAdHTML(728, 90, 'Fashion Advertisement'),
        sizes: [[728, 90]]
      },
      {
        key: 'banner-300x250',
        html: this.generateFallbackAdHTML(300, 250, 'Fashion Advertisement'),
        sizes: [[300, 250]]
      },
      {
        key: 'banner-320x50',
        html: this.generateFallbackAdHTML(320, 50, 'Fashion Ad'),
        sizes: [[320, 50]]
      }
    ];

    try {
      const cache = await caches.open('fashion-rating-fallback-ads-v1');
      
      const cachePromises = fallbackAds.map(ad => {
        const response = new Response(ad.html, {
          headers: { 
            'Content-Type': 'text/html',
            'X-Fallback-Ad': 'true',
            'X-Ad-Sizes': JSON.stringify(ad.sizes)
          }
        });
        return cache.put(`/fallback-ad/${ad.key}`, response);
      });

      await Promise.all(cachePromises);
      console.log('Offline ad fallbacks setup complete');
    } catch (error) {
      console.error('Failed to setup offline fallbacks:', error);
    }
  }

  /**
   * Generate fallback ad HTML
   */
  private generateFallbackAdHTML(width: number, height: number, text: string): string {
    const fontSize = Math.min(width / 20, height / 8, 16);
    const lines = text.split('\n');
    
    return `
      <div style="
        width: ${width}px;
        height: ${height}px;
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #6c757d;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: ${fontSize}px;
        font-weight: 500;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        flex-direction: column;
        text-align: center;
        padding: 8px;
        box-sizing: border-box;
        position: relative;
        overflow: hidden;
      ">
        <div style="
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, #007bff, #6610f2, #e83e8c, #fd7e14);
        "></div>
        
        <div style="margin-bottom: 4px; font-size: ${Math.min(fontSize * 1.5, 20)}px;">
          👗
        </div>
        
        ${lines.map(line => `<div style="line-height: 1.2;">${line}</div>`).join('')}
        
        <div style="
          font-size: ${Math.max(fontSize * 0.7, 10)}px;
          opacity: 0.7;
          margin-top: 4px;
        ">
          Discover your style
        </div>
      </div>
    `;
  }

  /**
   * Monitor ad cache performance
   */
  async monitorCachePerformance(): Promise<{
    hitRate: number;
    missRate: number;
    totalRequests: number;
  }> {
    // This would typically integrate with analytics
    // For now, return backend data
    return {
      hitRate: 0.75,
      missRate: 0.25,
      totalRequests: 100
    };
  }

  /**
   * Optimize cache based on usage patterns
   */
  async optimizeCache(): Promise<void> {
    if (!this.isSupported) return;

    try {
      const status = await this.getAdCacheStatus();
      if (!status) return;

      // If cache is getting full, clean up old entries
      if (status.totalCachedAds > 50) {
        console.log('Ad cache optimization: Cleaning up old entries');
        // This would typically remove least recently used entries
        // For now, just log the action
      }

      // Preload popular ad sizes based on usage
      const popularSizes = [
        [320, 50],  // Mobile banner
        [300, 250], // Medium rectangle
        [728, 90]   // Leaderboard
      ];

      console.log('Ad cache optimization: Ensuring popular sizes are cached');
    } catch (error) {
      console.error('Cache optimization failed:', error);
    }
  }

  /**
   * Check if service worker is supported
   */
  isServiceWorkerSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Get service worker registration status
   */
  async getRegistrationStatus(): Promise<{
    isRegistered: boolean;
    isActive: boolean;
    scope?: string;
  }> {
    if (!this.isSupported) {
      return { isRegistered: false, isActive: false };
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      return {
        isRegistered: !!registration,
        isActive: !!registration?.active,
        scope: registration?.scope
      };
    } catch (error) {
      return { isRegistered: false, isActive: false };
    }
  }

  /**
   * Listen for service worker messages
   */
  onServiceWorkerMessage(callback: (data: any) => void): void {
    if (!this.isSupported) return;

    navigator.serviceWorker.addEventListener('message', (event) => {
      callback(event.data);
    });
  }

  /**
   * Update service worker
   */
  async updateServiceWorker(): Promise<boolean> {
    if (!this.isSupported) return false;

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration) {
        await registration.update();
        console.log('Service worker updated');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to update service worker:', error);
      return false;
    }
  }
}

// Export singleton instance
export const adServiceWorkerIntegration = new AdServiceWorkerIntegration();